import React, { useState, ReactNode, useEffect, useRef } from 'react';
import { TFunction, withTranslation, WithTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import SplashBanner from '~/components/common/splash-banner';
import StatusBanner from '~/components/common/status-banner';
import {
    InnerContainer,
    MainContent,
    SkipLink,
    VisuallyHiddenElement,
} from '~/components/common/layout/styles';
import Header from '~/components/common/header';
import Footer from '~/components/common/footer';
import Spinner from '~/components/common/spinner';
import { isDashboardPage, shouldShowStatusBanner } from '~/utils/helpers';
import ApplicationHeader from '~/components/common/application-header';
import CreateAccountBanner from '~/components/common/create-account-banner';

interface BaseLayoutProps extends WithTranslation {
    showSplashBanner?: boolean;
    children: ReactNode;
    customAppTitle?: string;
    showCreateAccountBanner?: boolean;
    t: TFunction;
}

const BaseLayout = ({
    showSplashBanner,
    children,
    customAppTitle,
    showCreateAccountBanner,
    t,
}: BaseLayoutProps) => {
    const [pageTitle, setPageTitle] = useState('');
    const node = useRef<HTMLElement | null>(null);
    const router = useRouter();
    const { status } = useSession();

    useEffect(() => {
        /* This is to help with SPA accessibility.
            The user focuses on the <main> element when a new page is loaded, and an aria-live region is announced to the screen reader with the newly laoded page's title. 
         */
        setPageTitle(document.title);
        const updatePageFocus = () => {
            if (node.current) {
                node.current.focus();
            }
        };

        /* Ensures when a new page is routed to, the window is scrolled to the top */
        const scrollToTop = () => window.scrollTo(0, 0);

        router.events.on('routeChangeStart', updatePageFocus);
        router.events.on('routeChangeComplete', scrollToTop);

        // If the component is unmounted, unsubscribe from the event with the `off` method:
        return () => {
            router.events.off('routeChangeStart', updatePageFocus);
            router.events.off('routeChangeComplete', scrollToTop);
        };
    }, [router.events]);

    if (status === 'loading') {
        return <Spinner isLoading />;
    }

    return (
        <>
            <div>
                <SkipLink as="a" href="#content">
                    {t('base-layout:skipToMainContent.text')}
                </SkipLink>
                <div>
                    <Header />
                    <ApplicationHeader customAppTitle={customAppTitle} />
                    {shouldShowStatusBanner(router.pathname) && <StatusBanner />}
                    {showSplashBanner && <SplashBanner />}
                    {showCreateAccountBanner && <CreateAccountBanner />}
                </div>
                <MainContent
                    id="content"
                    role="main"
                    className="ontario-row"
                    ref={node}
                    $isDashboard={isDashboardPage(router.pathname)}
                >
                    <VisuallyHiddenElement
                        role="log"
                        aria-live="assertive"
                        aria-relevant="additions"
                        aria-atomic="true"
                    >
                        {t('base-layout:accessibilityBlock.text')} {pageTitle}
                    </VisuallyHiddenElement>
                    <InnerContainer>{children}</InnerContainer>
                </MainContent>
                <Footer role="contentinfo" />
            </div>
        </>
    );
};

export default withTranslation(['base-layout', 'help-chat'])(BaseLayout);
